import React from "react";
import { BatteryCharging } from "lucide-react";
import { COLOR } from "./pillars/_cards";
import { avgStateOfCharge } from "../../lib/batteryAnalytics";

/** Charge-Limit Discipline — turns the daily charge-limit setting into a
 *  longevity habit score. LFP packs get the opposite (100% weekly) advice. */
export const ChargeDisciplineCard = ({ vehicle }) => {
  const limit = typeof vehicle?.chargeLimit === "number" ? vehicle.chargeLimit : null;
  const lfp = /rwd|standard/i.test(vehicle?.trim || "") &&
    (vehicle?.model === "Model 3" || vehicle?.model === "Model Y");

  let grade, color, tip;
  if (limit == null) {
    grade = "—"; color = COLOR.zinc;
    tip = `Charge-limit reading arrives on the next ${vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"} sync.`;
  } else if (lfp) {
    grade = limit >= 95 ? "Ideal" : "Adjust";
    color = limit >= 95 ? COLOR.green : COLOR.cyan;
    tip = limit >= 95
      ? "Your LFP pack likes it — Tesla recommends charging LFP to 100% at least weekly to keep the SoC gauge calibrated."
      : "This is an LFP pack — unlike other chemistries, set the limit to 100% and charge fully at least once a week.";
  } else {
    grade = limit <= 80 ? "Excellent" : limit <= 90 ? "Good" : "Watch";
    color = limit <= 80 ? COLOR.green : limit <= 90 ? COLOR.amber : COLOR.red;
    tip = limit <= 80
      ? "Daily 80% limit is the single best longevity habit — high resting SoC is what ages cells fastest."
      : limit <= 90
      ? "Fine for trips — for daily use, dropping to 80% meaningfully slows calendar aging."
      : "Sitting above 90% daily accelerates fade (~0.3%/yr extra). Reserve full charges for departure days.";
  }

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}
      data-testid="charge-discipline-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: `${COLOR.green}14`, borderColor: `${COLOR.green}45` }}
        >
          <BatteryCharging className="w-4 h-4" style={{ color: COLOR.green }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Charge-limit discipline{lfp ? " · LFP" : ""}
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            {limit != null ? `Daily limit set to ${limit}%` : "Awaiting sync"}
          </div>
        </div>
        <span
          className="px-3 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wide shrink-0"
          style={{ color, background: `${color}18`, border: `1px solid ${color}55` }}
          data-testid="charge-discipline-grade"
        >
          {grade}
        </span>
      </div>
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
        <div>
          <div className="font-display text-xl text-white font-semibold leading-none">
            {avgStateOfCharge(vehicle)}<span className="font-mono text-[10px] text-zinc-400">%</span>
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">typical resting SoC</div>
        </div>
        <p className="font-mono text-[10px] text-zinc-400 leading-relaxed flex-1" data-testid="charge-discipline-tip">
          {tip}
        </p>
      </div>
    </div>
  );
};
